import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  UserPlus,
  Users as UsersIcon,
  UserCheck,
  MailWarning,
  UserMinus,
  Pencil,
  Send,
  Ban,
  Power,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Chip } from "@/components/kit/Chip";
import { DataTable } from "@/components/tables/DataTable";
import { RowActions } from "@/components/tables/RowActions";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { InviteLinkDialog } from "@/components/forms/InviteLinkDialog";
import { ExportButton } from "@/components/kit/ExportButton";
import { UserDrawer } from "./UserDrawer";
import { usersService, vaAgenciesService, warehousesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { ASSIGNABLE_ROLES, USER_STATUS_LABEL, USER_STATUS_TONE } from "@/lib/constants";
import { roleLabel } from "@/lib/permissions/roles";
import { isOwnerRole } from "@/lib/permissions/permissions";
import { useAuth } from "@/hooks/useAuth";

const DEFAULT_FILTERS = { search: "", role: "all", status: "all", agency: "all" };

export default function UsersPage() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const isOwner = isOwnerRole(currentUser?.role);
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [invite, setInvite] = useState(null);

  const params = useMemo(
    () => ({
      search: applied.search,
      role: applied.role === "all" ? "" : applied.role,
      status: applied.status === "all" ? "" : applied.status,
      va_agency_id: applied.agency === "all" ? "" : applied.agency,
    }),
    [applied]
  );

  const listQuery = useQuery({ queryKey: ["users", params], queryFn: () => usersService.list(params) });
  const summaryQuery = useQuery({ queryKey: ["users", "summary"], queryFn: usersService.summary });
  const agenciesQuery = useQuery({ queryKey: ["va-agencies", "lite"], queryFn: () => vaAgenciesService.list() });
  const warehousesQuery = useQuery({
    queryKey: ["warehouses", "lite"],
    queryFn: () => warehousesService.list(),
    enabled: isOwner,
  });

  const agencies = useMemo(() => agenciesQuery.data?.items || [], [agenciesQuery.data]);
  const agencyMap = useMemo(() => Object.fromEntries(agencies.map((a) => [a.id, a.name])), [agencies]);
  const warehouses = useMemo(() => warehousesQuery.data?.items || [], [warehousesQuery.data]);
  const warehouseMap = useMemo(() => Object.fromEntries(warehouses.map((w) => [w.id, w.name])), [warehouses]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["users"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
  };

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (row) => { setEditing(row); setDrawerOpen(true); };

  const handleSaved = (res) => {
    refresh();
    if (res?.invite_link) setInvite({ link: res.invite_link, name: res.user?.name, email: res.user?.email });
  };

  const doResend = async (row) => {
    try {
      const res = await usersService.resendInvite(row.id);
      refresh();
      setInvite({ link: res.invite_link, name: row.name, email: row.email });
      toast.success("Invitation regenerated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const doRevoke = async (row) => {
    try {
      await usersService.revokeInvite(row.id);
      toast.success("Invitation revoked");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const setStatus = async (row, status) => {
    try {
      await usersService.update(row.id, { status });
      toast.success(status === "active" ? "User activated" : "User deactivated");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const s = summaryQuery.data || {};
  const rows = listQuery.data?.items || [];

  const rowMenu = (r) => {
    const items = [{ key: "edit", label: "Edit", icon: Pencil, onSelect: () => openEdit(r) }];
    if (r.role === "PRODUCT_OWNER") return items; // protect platform owner
    if (["pending", "revoked"].includes(r.status))
      items.push({ key: "resend", label: "Resend invitation", icon: Send, separatorBefore: true, onSelect: () => doResend(r) });
    if (r.status === "pending")
      items.push({ key: "revoke", label: "Revoke invitation", icon: Ban, destructive: true, onSelect: () => doRevoke(r) });
    if (r.status === "active")
      items.push({ key: "deactivate", label: "Deactivate", icon: Power, destructive: true, separatorBefore: true, onSelect: () => setStatus(r, "inactive") });
    if (r.status === "inactive")
      items.push({ key: "activate", label: "Activate", icon: Power, separatorBefore: true, onSelect: () => setStatus(r, "active") });
    return items;
  };

  const columns = [
    {
      key: "name",
      header: "User",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.name}</p>
          <p className="text-xs text-muted-foreground">{r.email}</p>
        </div>
      ),
    },
    { key: "role", header: "Role", render: (r) => <Chip label={roleLabel(r.role)} tone="blue" /> },
    ...(isOwner
      ? [{
          key: "warehouse",
          header: "Warehouse",
          render: (r) => warehouseMap[r.warehouse_id] || <span className="text-muted-foreground">—</span>,
        }]
      : []),
    { key: "agency", header: "VA Agency", render: (r) => agencyMap[r.va_agency_id] || <span className="text-muted-foreground">—</span> },
    {
      key: "status",
      header: "Status",
      render: (r) => <StatusBadge label={USER_STATUS_LABEL[r.status] || r.status} tone={USER_STATUS_TONE[r.status] || "neutral"} />,
    },
    { key: "created", header: "Created", render: (r) => formatRelativeTime(r.created_at) },
    { key: "actions", header: "", align: "right", render: (r) => <RowActions items={rowMenu(r)} /> },
  ];

  return (
    <div className="space-y-6" data-testid="page-users">
      <PageHeader
        sticky
        title="Users"
        description="Invite teammates and manage roles across your organization."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton filename="users" rows={rows} testId="users-export" columns={[
              { label: "Name", key: "name" },
              { label: "Email", key: "email" },
              { label: "Role", get: (r) => roleLabel(r.role) },
              { label: "VA Agency", get: (r) => agencyMap[r.va_agency_id] || "" },
              { label: "Status", get: (r) => USER_STATUS_LABEL[r.status] || r.status },
              { label: "Created", key: "created_at" },
            ]} />
            <Button className="gap-2" onClick={openCreate} data-testid="users-primary-action">
              <UserPlus className="h-4 w-4" />
              Invite User
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-total-users" label="Total Users" value={s.total ?? "—"} icon={UsersIcon} tone="blue" />
        <StatCard testId="stat-active-users" label="Active" value={s.active ?? "—"} icon={UserCheck} tone="green" />
        <StatCard testId="stat-pending-users" label="Pending Invitations" value={s.pending ?? "—"} icon={MailWarning} tone="amber" />
        <StatCard testId="stat-inactive-users" label="Inactive" value={s.inactive ?? "—"} icon={UserMinus} tone="neutral" />
      </div>

      <FilterShell
        onApply={() => { setApplied(draft); toast.success("Filters applied"); }}
        onReset={() => { setDraft(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); toast("Filters reset"); }}
      >
        <SearchInput value={draft.search} onChange={(v) => setDraft((d) => ({ ...d, search: v }))} placeholder="Search users…" />
        <Select value={draft.role} onValueChange={(v) => setDraft((d) => ({ ...d, role: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ASSIGNABLE_ROLES.map((r) => (<SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending Invitation</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="revoked">Revoked</SelectItem>
          </SelectContent>
        </Select>
        <Select value={draft.agency} onValueChange={(v) => setDraft((d) => ({ ...d, agency: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-agency"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All agencies</SelectItem>
            {agencies.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : (
        <DataTable
          testId="users-table"
          paginated
          columns={columns}
          data={rows}
          emptyState={
            <EmptyState
              icon={UsersIcon}
              title="No users yet"
              description="Invite teammates and assign roles to collaborate in FulfillFlow."
              testId="users-empty-state"
              action={
                <Button className="gap-2" onClick={openCreate} data-testid="users-empty-action">
                  <UserPlus className="h-4 w-4" /> Invite User
                </Button>
              }
            />
          }
        />
      )}

      <UserDrawer open={drawerOpen} onOpenChange={setDrawerOpen} record={editing} agencies={agencies} warehouses={warehouses} onSaved={handleSaved} />
      <InviteLinkDialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)} link={invite?.link} name={invite?.name} email={invite?.email} />
    </div>
  );
}
